import React, { useState, useEffect, useCallback } from "react";
import { format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import {
  CalendarDays,
  Clock,
  Save,
  Send,
  Globe,
  Sparkles,
  FileText,
  Bell,
  Check,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface EventData {
  id: string | null;
  name: string;
  starts_at: string | null;
  ends_at: string | null;
  display_title: string;
  display_message: string;
  is_published: boolean;
}

interface ExhibitionEventBuilderProps {
  eventId: string | null;
  eventData?: {
    name: string | null;
    starts_at: string | null;
    ends_at: string | null;
    display_title: string | null;
    display_message: string | null;
    is_published: boolean;
  } | null;
  onEventUpdate: () => void;
}

const ExhibitionEventBuilder: React.FC<ExhibitionEventBuilderProps> = ({
  eventId,
  eventData: initialEventData,
  onEventUpdate,
}) => {
  const { user } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [sendNotification, setSendNotification] = useState(false);

  // Form state
  const [formData, setFormData] = useState<EventData>({
    id: eventId,
    name: initialEventData?.name ?? "Love Exhibition",
    starts_at: initialEventData?.starts_at ?? null,
    ends_at: initialEventData?.ends_at ?? null,
    display_title: initialEventData?.display_title ?? "",
    display_message: initialEventData?.display_message ?? "",
    is_published: initialEventData?.is_published ?? false,
  });

  // Derived date/time values for form inputs
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    formData.starts_at ? new Date(formData.starts_at) : undefined
  );
  const [startTime, setStartTime] = useState(
    formData.starts_at ? format(new Date(formData.starts_at), "HH:mm") : "12:00"
  );
  const [endTime, setEndTime] = useState(
    formData.ends_at ? format(new Date(formData.ends_at), "HH:mm") : "23:00"
  );

  // Track if we've initialized from props to prevent re-syncing on every parent render
  const [hasInitialized, setHasInitialized] = useState(false);
  const prevEventIdRef = React.useRef<string | null>(null);

  // Only sync from props on mount or when eventId actually changes
  useEffect(() => {
    const eventIdChanged = prevEventIdRef.current !== eventId;
    prevEventIdRef.current = eventId;

    // Only update if we haven't initialized yet, or if the event ID changed
    if (!hasInitialized || eventIdChanged) {
      if (initialEventData) {
        setFormData({
          id: eventId,
          name: initialEventData.name ?? "Love Exhibition",
          starts_at: initialEventData.starts_at,
          ends_at: initialEventData.ends_at,
          display_title: initialEventData.display_title ?? "",
          display_message: initialEventData.display_message ?? "",
          is_published: initialEventData.is_published ?? false,
        });
        if (initialEventData.starts_at) {
          setSelectedDate(new Date(initialEventData.starts_at));
          setStartTime(format(new Date(initialEventData.starts_at), "HH:mm"));
        }
        if (initialEventData.ends_at) {
          setEndTime(format(new Date(initialEventData.ends_at), "HH:mm"));
        }
        setIsDirty(false);
      }
      setHasInitialized(true);
    }
  }, [eventId, initialEventData, hasInitialized]);

  const handleFieldChange = (field: keyof EventData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const handleDateChange = (date: Date | undefined) => {
    setSelectedDate(date);
    setIsDirty(true);
  };

  const handleTimeChange = (type: "start" | "end", value: string) => {
    if (type === "start") {
      setStartTime(value);
    } else {
      setEndTime(value);
    }
    setIsDirty(true);
  };

  const buildTimestamps = () => {
    if (!selectedDate) return { starts_at: null, ends_at: null };

    const dateStr = format(selectedDate, "yyyy-MM-dd");
    // Create timestamps in ET timezone
    const startsAtStr = `${dateStr}T${startTime}:00`;
    const endsAtStr = `${dateStr}T${endTime}:00`;

    // Parse as ET and convert to UTC for storage
    const startsAt = new Date(`${startsAtStr}-05:00`).toISOString(); // EST offset (simplified)
    const endsAt = new Date(`${endsAtStr}-05:00`).toISOString();

    return { starts_at: startsAt, ends_at: endsAt };
  };

  const handleSave = async (publish: boolean) => {
    if (!selectedDate) {
      toast.error("Please select a date");
      return;
    }

    const { starts_at, ends_at } = buildTimestamps();
    if (!starts_at || !ends_at) {
      toast.error("Invalid date/time");
      return;
    }

    setIsLoading(true);

    try {
      if (eventId) {
        // Update existing event using the new RPC
        const { error } = await supabase.rpc("update_exhibition_schedule", {
          p_event_id: eventId,
          p_starts_at: starts_at,
          p_ends_at: ends_at,
          p_name: formData.name,
          p_display_title: formData.display_title || null,
          p_display_message: formData.display_message || null,
          p_is_published: publish,
          p_reason: publish ? "Event published" : "Draft saved",
        });

        if (error) throw error;
      } else {
        // Create new event
        const { data: newEvent, error: createError } = await supabase
          .from("exhibition_events")
          .insert({
            name: formData.name,
            starts_at: starts_at,
            ends_at: ends_at,
            display_title: formData.display_title || null,
            display_message: formData.display_message || null,
            is_published: publish,
            is_active: publish,
            created_by: user?.id,
          })
          .select()
          .single();

        if (createError) throw createError;

        // If we created a new event and want to send notifications
        if (publish && sendNotification && newEvent) {
          await sendEventNotifications(newEvent.name, starts_at);
        }
      }

      toast.success(publish ? "Event published!" : "Draft saved");
      setIsDirty(false);
      onEventUpdate();
    } catch (error) {
      console.error("Error saving event:", error);
      toast.error("Failed to save event");
    } finally {
      setIsLoading(false);
    }
  };

  const sendEventNotifications = async (eventName: string, startsAt: string) => {
    try {
      const { data: profiles } = await supabase.from("profiles").select("id");
      if (!profiles) return;

      const notifications = profiles.map((profile) => ({
        user_id: profile.id,
        type: "exhibition_event",
        title: "🌍 Live Dating Event Coming Up!",
        body: `${eventName} starts ${format(new Date(startsAt), "EEEE 'at' h:mm a")} ET`,
        data: { event_name: eventName, starts_at: startsAt },
      }));

      const batchSize = 100;
      for (let i = 0; i < notifications.length; i += batchSize) {
        const batch = notifications.slice(i, i + batchSize);
        await supabase.from("notifications").insert(batch);
      }

      toast.success("Notifications sent to all users");
    } catch (error) {
      console.error("Error sending notifications:", error);
    }
  };

  const characterCount = formData.display_message.length;
  const maxChars = 200;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="font-bold text-foreground text-lg">Next Live Dating Event</h3>
          <p className="text-sm text-muted-foreground">Schedule and customize your event</p>
        </div>
      </div>

      {/* Event Name */}
      <div className="space-y-2">
        <Label htmlFor="event-name" className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-muted-foreground" />
          Event Name
        </Label>
        <Input
          id="event-name"
          value={formData.name}
          onChange={(e) => handleFieldChange("name", e.target.value)}
          placeholder="Love Exhibition"
          className="bg-background"
        />
      </div>

      {/* Date & Time Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Date Picker */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-muted-foreground" />
            Date
          </Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !selectedDate && "text-muted-foreground"
                )}
              >
                <CalendarDays className="mr-2 h-4 w-4" />
                {selectedDate ? format(selectedDate, "MMM d, yyyy") : "Pick a date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleDateChange}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Start Time */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-muted-foreground" />
            Start Time
          </Label>
          <Input
            type="time"
            value={startTime}
            onChange={(e) => handleTimeChange("start", e.target.value)}
            className="bg-background"
          />
        </div>

        {/* End Time */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-muted-foreground" />
            End Time
          </Label>
          <Input
            type="time"
            value={endTime}
            onChange={(e) => handleTimeChange("end", e.target.value)}
            className="bg-background"
          />
        </div>
      </div>

      {/* Timezone indicator */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground px-3 py-2 rounded-lg bg-muted/50">
        <Globe className="w-4 h-4" />
        <span>All times are in <strong>ET (America/New_York)</strong></span>
      </div>

      {/* Announcement Section */}
      <div className="p-4 rounded-xl bg-gradient-to-br from-pink-500/5 to-rose-500/5 border border-pink-500/20 space-y-4">
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4 text-pink-500" />
          <span className="font-medium text-foreground">User Announcement</span>
        </div>

        <div className="space-y-2">
          <Label htmlFor="display-title">Announcement Title</Label>
          <Input
            id="display-title"
            value={formData.display_title}
            onChange={(e) => handleFieldChange("display_title", e.target.value)}
            placeholder="Love Exhibition is coming!"
            className="bg-background"
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="display-message">Announcement Message</Label>
            <span className={cn(
              "text-xs",
              characterCount > maxChars ? "text-red-500" : "text-muted-foreground"
            )}>
              {characterCount}/{maxChars}
            </span>
          </div>
          <Textarea
            id="display-message"
            value={formData.display_message}
            onChange={(e) => handleFieldChange("display_message", e.target.value)}
            placeholder="Join us for a special event where you can meet amazing people from around the world!"
            className="bg-background resize-none"
            rows={3}
            maxLength={maxChars}
          />
        </div>
      </div>

      {/* Send Notification Toggle (only for new events) */}
      {!eventId && (
        <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
          <div className="flex items-center gap-2">
            <Send className="w-4 h-4 text-blue-500" />
            <div>
              <p className="text-sm font-medium">Send Push Notifications</p>
              <p className="text-xs text-muted-foreground">Notify all users when published</p>
            </div>
          </div>
          <Button
            variant={sendNotification ? "default" : "outline"}
            size="sm"
            onClick={() => setSendNotification(!sendNotification)}
          >
            {sendNotification ? <Check className="w-4 h-4" /> : "Enable"}
          </Button>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button
          variant="outline"
          className="flex-1 gap-2"
          onClick={() => handleSave(false)}
          disabled={isLoading || !selectedDate}
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Save Draft
        </Button>
        <Button
          className="flex-1 gap-2 bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600"
          onClick={() => handleSave(true)}
          disabled={isLoading || !selectedDate}
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
          Publish Event
        </Button>
      </div>

      {/* Status indicator */}
      {formData.is_published && (
        <div className="flex items-center gap-2 text-sm text-green-600 bg-green-500/10 px-3 py-2 rounded-lg">
          <Check className="w-4 h-4" />
          <span>This event is published and visible to users</span>
        </div>
      )}

      {isDirty && (
        <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-500/10 px-3 py-2 rounded-lg">
          <AlertTriangle className="w-4 h-4" />
          <span>You have unsaved changes</span>
        </div>
      )}
    </div>
  );
};

export default ExhibitionEventBuilder;